@if (isPreview() && isPhoneScreen()) {
    <div class="mb-3">
        <app-restaurant-information [diagnostic]="diagnostic()" [isScorePreview]="true"></app-restaurant-information>
    </div>
}

@if (partnerTheme(); as theme) {
    <!-- Partner-themed score card -->
    <div
        class="partner-score-card overflow-hidden rounded-[5px] border border-[#eff0f7]"
        [style.background-color]="theme.header.primaryColor">
        <!-- Left dark panel: title + gauge + score -->
        <div class="partner-score-card__left">
            <div class="partner-score-card__title font-futura-bt uppercase text-white">
                {{ 'maloupe.diagnostic_score.title_partner' | translate }}
            </div>
            <div class="partner-score-card__gauge-area">
                <img
                    class="partner-score-card__gauge-img"
                    alt="Score gauge"
                    [src]="scoreGaugeImage() | imagePathResolver: { folder: 'score-gauge' }" />
                <div class="partner-score-card__score">
                    <span
                        class="partner-score-card__score-number"
                        [ngClass]="{
                            'text-malou-state-error': scoreRating() === DiagnosticRating.BAD,
                            'text-malou-yellow': scoreRating() === DiagnosticRating.AVERAGE,
                            'text-malou-green': scoreRating() === DiagnosticRating.GOOD,
                        }"
                        >{{ score() }}</span
                    >
                    <span class="partner-score-card__score-denom text-white/70">/100</span>
                </div>
            </div>
        </div>

        <!-- Right white panel: headline + rating rows -->
        <div class="partner-score-card__right">
            <div class="partner-score-card__headline">
                <ng-container [ngTemplateOutlet]="partnerScoreHeaderTemplate"></ng-container>
            </div>
            <div class="partner-score-card__rows">
                <ng-container [ngTemplateOutlet]="partnerRatingRowInconsistenciesTemplate"></ng-container>
                <ng-container [ngTemplateOutlet]="partnerRatingRowCompetitorsTemplate"></ng-container>
                <ng-container [ngTemplateOutlet]="partnerRatingRowReviewsTemplate"></ng-container>
                @if (hasInstagram()) {
                    <ng-container [ngTemplateOutlet]="partnerRatingRowInstagramTemplate"></ng-container>
                }
            </div>
        </div>
    </div>
} @else {
    <div
        class="md:p-6m flex h-full flex-col gap-4 rounded-[20px] p-8 sm:px-4 md:gap-0"
        [ngClass]="{
            'bg-malou-pink--light': scoreRating() === DiagnosticRating.BAD,
            'bg-malou-yellow--light': scoreRating() === DiagnosticRating.AVERAGE,
            'bg-malou-green--light': scoreRating() === DiagnosticRating.GOOD,
            '!flex-row !gap-8 sm:!flex-col': !isPreview(),
        }">
        <ng-container [ngTemplateOutlet]="scoreTemplate"></ng-container>
        @if (isPreview() && !isPhoneScreen()) {
            <app-restaurant-information
                [diagnostic]="diagnostic()"
                [scoreRating]="scoreRating()"
                [isScorePreview]="true"></app-restaurant-information>
        }
        <ng-container [ngTemplateOutlet]="scoreDetailsTemplate"></ng-container>
    </div>
}

<ng-template #scoreTemplate>
    <div
        class="flex w-[30%] gap-6 md:w-full md:gap-0"
        [ngClass]="{ '!w-full !flex-row !justify-between sm:!flex-col': isPreview(), 'flex-col': !isPreview() }">
        <div class="flex justify-center text-malou-text-title">
            <div class="malou-text-50--bold md:malou-text-20--bold mt-3 font-passion-one uppercase md:mt-0">
                <span class="relative">
                    <span [innerHTML]="'maloupe.diagnostic_score.title' | translate"></span>
                    <mat-icon
                        class="absolute bottom-[40px] right-[-30px] !h-10 !w-10 shrink-0 text-malou-pink md:bottom-2.5 md:right-[-18px] md:!inline-block md:!h-7 md:!w-7"
                        [svgIcon]="SvgIcon.SPARKLES"></mat-icon>
                </span>
            </div>
        </div>
        <div
            class="flex items-center justify-center gap-4 md:flex-row"
            [ngClass]="{ 'flex-row sm:flex-col': isPreview(), 'flex-col': !isPreview() }">
            <img
                class="h-44 w-48 md:h-20 md:w-24"
                alt="Score gauge"
                inline="true"
                [src]="scoreGaugeImage() | imagePathResolver: { folder: 'score-gauge' }" />
            <div class="malou-text-20--regular flex items-baseline text-malou-text-title/70">
                <span
                    class="malou-text-70--bold md:malou-text-60--bold"
                    [ngClass]="{
                        'text-malou-pink': scoreRating() === DiagnosticRating.BAD,
                        'text-malou-yellow': scoreRating() === DiagnosticRating.AVERAGE,
                        'text-malou-green': scoreRating() === DiagnosticRating.GOOD,
                    }"
                    >{{ score() }}</span
                >
                <span class="min-w-fit">/ 100</span>
            </div>
        </div>
    </div>
</ng-template>

<ng-template #scoreDetailsTemplate>
    <div class="flex w-full flex-col gap-y-7.5 rounded-[20px] bg-white/20 p-10 pt-5 md:gap-y-5 md:p-5">
        <ng-container [ngTemplateOutlet]="scoreDetailsHeaderTemplate"></ng-container>
        <ng-container [ngTemplateOutlet]="scoreDetailsDescriptionTemplate"></ng-container>

        <ng-container [ngTemplateOutlet]="scoreDetailsInconsistenciesTemplate"></ng-container>
        <ng-container [ngTemplateOutlet]="scoreDetailsBetterLocalCompetitorsTemplate"></ng-container>
        <ng-container [ngTemplateOutlet]="scoreDetailsReviewCountTemplate"></ng-container>

        @if (hasInstagram()) {
            @switch (instagramVisibilityRating()) {
                @case (DiagnosticRating.BAD) {
                    <ng-container [ngTemplateOutlet]="scoreDetailsInstagramBadVisibilityTemplate"></ng-container>
                }
                @case (DiagnosticRating.AVERAGE) {
                    <ng-container [ngTemplateOutlet]="scoreDetailsInstagramAverageVisibilityTemplate"></ng-container>
                }
                @case (DiagnosticRating.GOOD) {
                    <ng-container [ngTemplateOutlet]="scoreDetailsInstagramGoodVisibilityTemplate"></ng-container>
                }
            }
        }
    </div>
</ng-template>

<ng-template #scoreDetailsHeaderTemplate>
    <div class="flex items-center gap-x-2">
        <img class="w-28 md:w-16" [src]="Illustration.BINOCULARS | illustrationPathResolver" />
        <span class="malou-text-23--medium md:malou-text-12--medium text-malou-text-title" id="scoreDetailsHeader">
            @switch (scoreRating()) {
                @case (DiagnosticRating.BAD) {
                    <span [innerHTML]="'maloupe.diagnostic_score.header.bad_title' | translate"></span>
                }
                @case (DiagnosticRating.AVERAGE) {
                    <span [innerHTML]="'maloupe.diagnostic_score.header.average_title' | translate"></span>
                }
                @case (DiagnosticRating.GOOD) {
                    <span [innerHTML]="'maloupe.diagnostic_score.header.good_title' | translate"></span>
                }
            }
        </span>
    </div>
</ng-template>

<ng-template #scoreDetailsInconsistenciesTemplate>
    <app-rating-bloc
        [text]="'maloupe.diagnostic_score.inconsistencies.text' | pluralTranslate: inconsistencyCount()"
        [title]="'maloupe.diagnostic_score.inconsistencies.title' | pluralTranslate: inconsistencyCount()"
        [rating]="inconsistenciesRating()"></app-rating-bloc>
</ng-template>

<ng-template #scoreDetailsDescriptionTemplate>
    <span class="malou-text-23--bold md:malou-text-12--bold text-malou-text-title" id="scoreDetailsDesctiption">
        {{ 'maloupe.diagnostic_score.description' | translate }}
    </span>
</ng-template>

<ng-template #scoreDetailsBetterLocalCompetitorsTemplate>
    <app-rating-bloc
        [text]="'maloupe.diagnostic_score.better_local_competitors.text' | pluralTranslate: betterLocalCompetitors()"
        [title]="'maloupe.diagnostic_score.better_local_competitors.title' | pluralTranslate: betterLocalCompetitors()"
        [rating]="googleRating()"></app-rating-bloc>
</ng-template>

<ng-template #scoreDetailsReviewCountTemplate>
    @switch (reviewCountRating()) {
        @case (DiagnosticRating.BAD) {
            @if (reviewCount() === 0) {
                <app-rating-bloc
                    [text]="'maloupe.diagnostic_score.review_count.no_review_text' | translate"
                    [title]="'maloupe.diagnostic_score.review_count.title.no_reviews' | translate"
                    [rating]="reviewCountRating()"></app-rating-bloc>
            } @else {
                <app-rating-bloc
                    [text]="'maloupe.diagnostic_score.review_count.text' | translate"
                    [title]="'maloupe.diagnostic_score.review_count.title.bad' | translate: { reviewCountDiff: reviewCountDiff() }"
                    [rating]="reviewCountRating()"></app-rating-bloc>
            }
        }
        @case (DiagnosticRating.AVERAGE) {
            <app-rating-bloc
                [text]="'maloupe.diagnostic_score.review_count.text' | translate"
                [title]="'maloupe.diagnostic_score.review_count.title.average' | translate: { reviewCountDiff: reviewCountDiff() }"
                [rating]="reviewCountRating()"></app-rating-bloc>
        }
        @case (DiagnosticRating.GOOD) {
            <app-rating-bloc
                [text]="'maloupe.diagnostic_score.review_count.text' | translate"
                [title]="'maloupe.diagnostic_score.review_count.title.good' | translate"
                [rating]="reviewCountRating()"></app-rating-bloc>
        }
    }
</ng-template>

<ng-template #scoreDetailsInstagramBadVisibilityTemplate>
    <app-rating-bloc
        [title]="'maloupe.diagnostic_score.instagram_visibility.title.bad' | translate"
        [text]="'maloupe.diagnostic_score.instagram_visibility.text.bad' | translate"
        [rating]="instagramVisibilityRating()"></app-rating-bloc>
</ng-template>

<ng-template #scoreDetailsInstagramAverageVisibilityTemplate>
    <app-rating-bloc
        [title]="'maloupe.diagnostic_score.instagram_visibility.title.average' | translate"
        [text]="'maloupe.diagnostic_score.instagram_visibility.text.average' | translate"
        [rating]="instagramVisibilityRating()"></app-rating-bloc>
</ng-template>

<ng-template #scoreDetailsInstagramGoodVisibilityTemplate>
    <app-rating-bloc
        [title]="'maloupe.diagnostic_score.instagram_visibility.title.good' | translate"
        [text]="'maloupe.diagnostic_score.instagram_visibility.text.good' | translate"
        [rating]="instagramVisibilityRating()"></app-rating-bloc>
</ng-template>

<!-- ── Partner-themed templates ─────────────────────────────────── -->

<ng-template #partnerScoreHeaderTemplate>
    <div class="partner-score-card__header-content">
        <img class="partner-score-card__gimmick" alt="" [src]="'logo' | logoPathResolver: { folder: 'partners' }" />
        <span class="partner-score-card__header-text text-malou-text-title">
            @switch (scoreRating()) {
                @case (DiagnosticRating.BAD) {
                    <span [innerHTML]="'maloupe.diagnostic_score.header_partner.bad_title' | translate"></span>
                }
                @case (DiagnosticRating.AVERAGE) {
                    <span [innerHTML]="'maloupe.diagnostic_score.header_partner.average_title' | translate"></span>
                }
                @case (DiagnosticRating.GOOD) {
                    <span [innerHTML]="'maloupe.diagnostic_score.header_partner.good_title' | translate"></span>
                }
            }
        </span>
    </div>
</ng-template>

<ng-template #partnerRatingRowInconsistenciesTemplate>
    <app-rating-bloc
        [isInverted]="true"
        [title]="'maloupe.diagnostic_score.inconsistencies.title' | pluralTranslate: inconsistencyCount()"
        [text]="'maloupe.diagnostic_score.inconsistencies.text' | pluralTranslate: inconsistencyCount()"
        [rating]="inconsistenciesRating()"></app-rating-bloc>
</ng-template>

<ng-template #partnerRatingRowCompetitorsTemplate>
    <app-rating-bloc
        [isInverted]="true"
        [title]="'maloupe.diagnostic_score.better_local_competitors.title' | pluralTranslate: betterLocalCompetitors()"
        [text]="'maloupe.diagnostic_score.better_local_competitors.text' | pluralTranslate: betterLocalCompetitors()"
        [rating]="googleRating()"></app-rating-bloc>
</ng-template>

<ng-template #partnerRatingRowReviewsTemplate>
    @switch (reviewCountRating()) {
        @case (DiagnosticRating.BAD) {
            @if (reviewCount() === 0) {
                <app-rating-bloc
                    [isInverted]="true"
                    [title]="'maloupe.diagnostic_score.review_count.title.no_reviews' | translate"
                    [text]="'maloupe.diagnostic_score.review_count.no_review_text' | translate"
                    [rating]="reviewCountRating()"></app-rating-bloc>
            } @else {
                <app-rating-bloc
                    [isInverted]="true"
                    [title]="'maloupe.diagnostic_score.review_count.title.bad' | translate: { reviewCountDiff: reviewCountDiff() }"
                    [text]="'maloupe.diagnostic_score.review_count.text' | translate"
                    [rating]="reviewCountRating()"></app-rating-bloc>
            }
        }
        @case (DiagnosticRating.AVERAGE) {
            <app-rating-bloc
                [isInverted]="true"
                [title]="'maloupe.diagnostic_score.review_count.title.average' | translate: { reviewCountDiff: reviewCountDiff() }"
                [text]="'maloupe.diagnostic_score.review_count.text' | translate"
                [rating]="reviewCountRating()"></app-rating-bloc>
        }
        @case (DiagnosticRating.GOOD) {
            <app-rating-bloc
                [isInverted]="true"
                [title]="'maloupe.diagnostic_score.review_count.title.good' | translate"
                [text]="'maloupe.diagnostic_score.review_count.text' | translate"
                [rating]="reviewCountRating()"></app-rating-bloc>
        }
    }
</ng-template>

<ng-template #partnerRatingRowInstagramTemplate>
    @switch (instagramVisibilityRating()) {
        @case (DiagnosticRating.BAD) {
            <app-rating-bloc
                [isInverted]="true"
                [title]="'maloupe.diagnostic_score.instagram_visibility.title.bad' | translate"
                [text]="'maloupe.diagnostic_score.instagram_visibility.text.bad' | translate"
                [rating]="instagramVisibilityRating()"></app-rating-bloc>
        }
        @case (DiagnosticRating.AVERAGE) {
            <app-rating-bloc
                [isInverted]="true"
                [title]="'maloupe.diagnostic_score.instagram_visibility.title.average' | translate"
                [text]="'maloupe.diagnostic_score.instagram_visibility.text.average' | translate"
                [rating]="instagramVisibilityRating()"></app-rating-bloc>
        }
        @case (DiagnosticRating.GOOD) {
            <app-rating-bloc
                [isInverted]="true"
                [title]="'maloupe.diagnostic_score.instagram_visibility.title.good' | translate"
                [text]="'maloupe.diagnostic_score.instagram_visibility.text.good' | translate"
                [rating]="instagramVisibilityRating()"></app-rating-bloc>
        }
    }
</ng-template>
